import { useEffect, useRef } from 'react';

const CustomCursor = () => {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const mouse = useRef({ x: -100, y: -100 });
  const dot = useRef({ x: -100, y: -100 });
  const ring = useRef({ x: -100, y: -100 });
  const hovering = useRef(false);
  const raf = useRef(null);

  useEffect(() => {
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) return;

    // Hide default cursor everywhere
    const style = document.createElement('style');
    style.textContent = '*, *::before, *::after { cursor: none !important; }';
    document.head.appendChild(style);

    const onMove = (e) => {
      mouse.current.x = e.clientX;
      mouse.current.y = e.clientY;

      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (!el) return;
      hovering.current = !!el.closest('a, button, [role="button"], input, textarea, select, [data-cursor-hover]');
    };

    const tick = () => {
      // Dot follows fast (near-instant)
      dot.current.x += (mouse.current.x - dot.current.x) * 0.35;
      dot.current.y += (mouse.current.y - dot.current.y) * 0.35;

      // Ring trails behind — smooth, organic lag
      ring.current.x += (mouse.current.x - ring.current.x) * 0.12;
      ring.current.y += (mouse.current.y - ring.current.y) * 0.12;

      if (dotRef.current) {
        const s = hovering.current ? 0 : 1;
        dotRef.current.style.transform = `translate3d(${dot.current.x}px, ${dot.current.y}px, 0) scale(${s})`;
      }
      if (ringRef.current) {
        const s = hovering.current ? 1.8 : 1;
        ringRef.current.style.transform = `translate3d(${ring.current.x}px, ${ring.current.y}px, 0) scale(${s})`;
        ringRef.current.style.borderColor = hovering.current
          ? 'rgba(197, 165, 90, 0.6)'
          : 'rgba(197, 165, 90, 0.25)';
      }

      raf.current = requestAnimationFrame(tick);
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    raf.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(raf.current);
      style.remove();
    };
  }, []);

  const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
  if (isTouchDevice) return null;

  const shared = { position: 'fixed', top: 0, left: 0, pointerEvents: 'none', borderRadius: '50%', willChange: 'transform' };

  return (
    <>
      {/* Inner dot — fast, solid */}
      <div
        ref={dotRef}
        data-testid="custom-cursor"
        style={{
          ...shared,
          zIndex: 99999,
          width: 8,
          height: 8,
          marginLeft: -4,
          marginTop: -4,
          backgroundColor: '#C5A55A',
          transition: 'opacity 0.2s',
        }}
      />
      {/* Outer ring — trails smoothly */}
      <div
        ref={ringRef}
        style={{
          ...shared,
          zIndex: 99998,
          width: 32,
          height: 32,
          marginLeft: -16,
          marginTop: -16,
          backgroundColor: 'transparent',
          border: '1.5px solid rgba(197, 165, 90, 0.25)',
          transition: 'border-color 0.3s, transform 0.15s',
        }}
      />
    </>
  );
};

export default CustomCursor;
